import { ActivatedRoute, Router } from '@angular/router';
import {
  clearedQueryParams, compactFilters, FilterSpec, FilterState, filtersFromQueryParams, filtersToQueryParams,
  forgetFilters, readRememberedFilters, rememberFilters
} from './filter-state';

/**
 * The signed-in user's id, read the same way `BaseService` reads it. Returns null when nobody is signed
 * in or storage is unavailable, in which case nothing is remembered.
 */
export function currentUserKey(): number | null {
  try {
    const raw = localStorage.getItem('currentUser');
    if (!raw || raw === 'undefined' || raw === 'null') {
      return null;
    }
    const id = JSON.parse(raw)?.id;
    return id === null || id === undefined ? null : id;
  } catch {
    return null;
  }
}

/**
 * Remember-my-filters and filter state in the URL for one screen. The screen keeps its own form, and
 * calls this only while its `filters.<screen>` switch is on:
 *
 * - `initialState()` when the switch turns on: the URL's filters if it has any, else the user's
 *   remembered filters, else null (keep the screen's defaults).
 * - `save(state)` on every search: remembers it and puts it in the URL, replacing the history entry.
 * - `clear()` on Clear all: forgets it and takes the filters out of the URL.
 *
 * Only the keys in `spec` are handled. Other query params, a tab for instance, are left as they are.
 */
export class ScreenFilterUx {
  constructor(
    private readonly screenKey: string,
    private readonly spec: FilterSpec,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly userKey: () => number | null = currentUserKey
  ) { }

  public initialState(): FilterState | null {
    return this.urlState() ?? this.rememberedState();
  }

  /** The filters in the URL, or null when it carries none. */
  public urlState(): FilterState | null {
    return filtersFromQueryParams(this.route?.snapshot?.queryParams ?? {}, this.spec);
  }

  /** This user's remembered filters for the screen, or null. */
  public rememberedState(): FilterState | null {
    const remembered = readRememberedFilters(this.screenKey, this.userKey());
    if (!remembered) {
      return null;
    }
    // A remembered entry may predate a change to the screen's filters; keep only what it still has.
    const known: FilterState = {};
    Object.keys(this.spec).forEach(key => {
      if (key in remembered) {
        known[key] = remembered[key];
      }
    });
    return Object.keys(known).length > 0 ? known : null;
  }

  public save(state: FilterState): void {
    rememberFilters(this.screenKey, this.userKey(), state);
    this.navigate({ ...clearedQueryParams(this.spec), ...filtersToQueryParams(compactFilters(state), this.spec) });
  }

  public clear(): void {
    forgetFilters(this.screenKey, this.userKey());
    this.navigate(clearedQueryParams(this.spec));
  }

  private navigate(queryParams: Record<string, any>): void {
    if (typeof this.router?.navigate !== 'function') {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route ?? undefined,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
